import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  CreditCard,
  Check,
  Package,
  Shield,
  Star,
  Globe,
  ChevronRight,
  Phone,
  Mail,
  Car,
  Loader2,
  CheckCircle,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PACKAGES = {
  essentiel: {
    name: "Pack Essentiel",
    price: 990,
    payment_type: "full",
    icon: Package,
    color: "#3b82f6",
    features: ["Recherche personnalisée", "Sélection de 3 véhicules", "Rapport d'état"],
  },
  avance: {
    name: "Pack Avancé",
    price: 1290,
    payment_type: "full",
    icon: Shield,
    color: "#8b5cf6",
    features: ["Tout Pack Essentiel", "Inspection mécanique", "Négociation prix", "Accompagnement achat"],
  },
  securite: {
    name: "Pack Sécurité",
    price: 2490,
    deposit: 747,
    payment_type: "deposit",
    icon: Shield,
    color: "#22c55e",
    features: ["Tout Pack Avancé", "Garantie 6 mois", "Rapatriement véhicule", "Carte grise incluse"],
  },
  premium: {
    name: "Pack Premium",
    price: 3990,
    deposit: 1197,
    payment_type: "deposit",
    icon: Star,
    color: "#f59e0b",
    features: ["Service VIP", "Garantie 12 mois", "Livraison à domicile", "Assistance 24/7"],
  },
  coree: {
    name: "Import Corée",
    price: 3990,
    deposit: 1197,
    payment_type: "deposit",
    icon: Globe,
    color: "#06b6d4",
    features: ["Import complet Corée", "Inspection sur place", "Transport maritime", "Dédouanement"],
  },
};

const PaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [step, setStep] = useState(sessionId ? "success" : "select");
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    vehicle: "",
  });

  // Poll payment status if returning from Stripe
  useEffect(() => {
    if (sessionId && step === "success") {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 5;

    if (attempts >= maxAttempts) {
      setPaymentStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sid}`);
      
      if (response.data.payment_status === "paid") {
        setPaymentStatus("success");
        toast.success("Paiement confirmé ! Merci pour votre réservation.");
      } else if (response.data.status === "expired") {
        setPaymentStatus("expired");
      } else {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setPaymentStatus("error");
    }
  };

  const handlePackageSelect = (packageId) => {
    setSelectedPackage(packageId);
    setStep("form");
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/checkout/create`, {
        package_id: selectedPackage,
        origin_url: window.location.origin,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        vehicle_info: formData.vehicle,
      });

      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Erreur lors de la création du paiement");
      setLoading(false);
    }
  };

  // Success page
  if (step === "success") {
    return (
      <div data-testid="payment-success" className="pt-40 min-h-screen">
        <section className="py-24 px-6 md:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-zinc-900/50 border border-neon-lime/50 p-12 backdrop-blur-sm">
              {paymentStatus === "success" ? (
                <>
                  <CheckCircle className="w-20 h-20 text-neon-lime mx-auto mb-6" />
                  <h2 className="font-unbounded text-3xl font-bold text-white mb-4">
                    Réservation confirmée !
                  </h2>
                  <p className="text-zinc-400 mb-8">
                    Merci pour votre confiance ! Nous vous contacterons très rapidement pour démarrer votre projet.
                  </p>
                </>
              ) : paymentStatus === "timeout" || paymentStatus === "error" ? (
                <>
                  <CheckCircle className="w-20 h-20 text-yellow-500 mx-auto mb-6" />
                  <h2 className="font-unbounded text-3xl font-bold text-white mb-4">
                    Vérification en cours
                  </h2>
                  <p className="text-zinc-400 mb-8">
                    Votre paiement est en cours de traitement. Vous recevrez une confirmation par email.
                  </p>
                </>
              ) : (
                <>
                  <Loader2 className="w-20 h-20 text-neon-lime mx-auto mb-6 animate-spin" />
                  <h2 className="font-unbounded text-3xl font-bold text-white mb-4">
                    Vérification du paiement...
                  </h2>
                </>
              )}
              <Button
                onClick={() => navigate("/")}
                className="bg-neon-lime text-black font-unbounded font-bold uppercase px-8 py-6"
              >
                Retour à l'accueil
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="payment-page" className="pt-32 md:pt-40">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-carbon-paper border-b border-zinc-800 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage: "url('/assets/Fast & Car/Fond Carbon.jpeg')",
            backgroundSize: "200px",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <p className="font-mono text-neon-lime text-sm tracking-widest uppercase mb-4">
              Réservation en ligne
            </p>
            <h1 className="font-unbounded text-4xl md:text-6xl font-black text-white leading-tight mb-6">
              Réservez votre
              <br />
              <span className="text-neon-lime text-glow">prestation</span>
            </h1>
            <p className="font-manrope text-lg md:text-xl text-zinc-400 leading-relaxed">
              Paiement sécurisé par Stripe. Acompte de 30% pour les packs Sécurité, Premium et Import Corée.
            </p>
          </div>
        </div>
      </section>

      {/* Package Selection */}
      {step === "select" && (
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(PACKAGES).map(([id, pkg]) => {
                const Icon = pkg.icon;
                const isDeposit = pkg.payment_type === "deposit";
                const displayPrice = isDeposit ? pkg.deposit : pkg.price;
                
                return (
                  <div
                    key={id}
                    data-testid={`package-${id}`}
                    className="bg-zinc-900/50 border border-zinc-800 hover:border-neon-lime/50 transition-all cursor-pointer group"
                    onClick={() => handlePackageSelect(id)}
                  >
                    <div className="p-6 border-b border-zinc-800" style={{ borderTopColor: pkg.color, borderTopWidth: "4px" }}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: `${pkg.color}20` }}>
                          <Icon className="w-6 h-6" style={{ color: pkg.color }} />
                        </div>
                        <div>
                          <h3 className="font-unbounded text-lg font-bold text-white">{pkg.name}</h3>
                          {isDeposit && (
                            <span className="text-xs text-zinc-500">Acompte 30%</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <span className="font-unbounded text-3xl font-bold text-white">{displayPrice}€</span>
                        {isDeposit && (
                          <span className="text-zinc-500 text-sm ml-2">/ {pkg.price}€ total</span>
                        )}
                      </div>
                      
                      <ul className="space-y-2">
                        {pkg.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-zinc-400">
                            <Check className="w-4 h-4 text-neon-lime flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="p-4 flex items-center justify-between bg-zinc-800/30 group-hover:bg-neon-lime/10 transition-colors">
                      <span className="text-zinc-400 text-sm">Sélectionner</span>
                      <ChevronRight className="w-5 h-5 text-neon-lime" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Payment Form */}
      {step === "form" && selectedPackage && (
        <section className="py-16 md:py-24">
          <div className="max-w-2xl mx-auto px-6 md:px-12">
            <div className="bg-zinc-900/50 border border-zinc-800 p-8 backdrop-blur-sm">
              {/* Selected package summary */}
              <div className="mb-8 p-4 bg-zinc-800/50 border-l-4" style={{ borderLeftColor: PACKAGES[selectedPackage].color }}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-unbounded text-lg font-bold text-white">
                      {PACKAGES[selectedPackage].name}
                    </h3>
                    <p className="text-zinc-500 text-sm">
                      {PACKAGES[selectedPackage].payment_type === "deposit" ? "Acompte 30%" : "Paiement complet"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-unbounded text-2xl font-bold text-neon-lime">
                      {PACKAGES[selectedPackage].payment_type === "deposit" 
                        ? PACKAGES[selectedPackage].deposit 
                        : PACKAGES[selectedPackage].price}€
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handlePayment} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-white flex items-center gap-2">
                      <span>Nom complet *</span>
                    </Label>
                    <Input
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleFormChange}
                      placeholder="Jean Dupont"
                      data-testid="payment-name"
                      className="bg-zinc-800 border-zinc-700 text-white h-12 rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white flex items-center gap-2">
                      <Phone className="w-4 h-4 text-neon-lime" />
                      Téléphone *
                    </Label>
                    <Input
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleFormChange}
                      placeholder="06 XX XX XX XX"
                      data-testid="payment-phone"
                      className="bg-zinc-800 border-zinc-700 text-white h-12 rounded-none"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-2">
                    <Mail className="w-4 h-4 text-neon-lime" />
                    Email *
                  </Label>
                  <Input
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleFormChange}
                    placeholder="jean@example.com"
                    data-testid="payment-email"
                    className="bg-zinc-800 border-zinc-700 text-white h-12 rounded-none"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-2">
                    <Car className="w-4 h-4 text-neon-lime" />
                    Véhicule recherché (optionnel)
                  </Label>
                  <Input
                    name="vehicle"
                    value={formData.vehicle}
                    onChange={handleFormChange}
                    placeholder="Ex: BMW M3 2020, Audi RS6 2019..."
                    data-testid="payment-vehicle"
                    className="bg-zinc-800 border-zinc-700 text-white h-12 rounded-none"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="button"
                    onClick={() => setStep("select")}
                    variant="outline"
                    className="flex-1 border-zinc-700 text-white"
                  >
                    Retour
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="payment-submit"
                    className="flex-1 bg-neon-lime text-black font-unbounded font-bold uppercase"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Payer
                      </>
                    )}
                  </Button>
                </div>
              </form>

              <div className="mt-6 flex items-center justify-center gap-2 text-zinc-500 text-xs">
                <Shield className="w-4 h-4" />
                Paiement sécurisé par Stripe
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default PaymentPage;
